import { useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { getApiErrorMessage, getApiErrorStatus } from '@/lib/api/error';
import { useUploadMediaAssetMutation } from '../hooks/useMediaMutations';
import { MEDIA_INPUT_ACCEPT } from '../utils/constants';

interface MediaUploadButtonProps {
  disabled?: boolean;
  size?: 'default' | 'sm' | 'lg';
  variant?: 'default' | 'outline';
}

function mapUploadError(error: unknown): string {
  const status = getApiErrorStatus(error);
  if (status === 413) return 'media.upload.err_too_large';
  if (status === 415) return 'media.upload.err_type';
  if (status === 409) return 'media.upload.err_quota';
  return 'media.upload.err_generic';
}

/**
 * "Upload photo" button backed by a hidden `<input type="file">`. Uploads go
 * straight to the library; the list query is invalidated on success so the
 * new asset appears wherever the library is shown.
 */
export function MediaUploadButton({
  disabled = false,
  size = 'default',
  variant = 'default',
}: MediaUploadButtonProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useUploadMediaAssetMutation({
    onSuccess: ({ deduped }) => {
      toast.success(deduped ? t('media.upload.toast_deduped') : t('media.upload.toast_added'));
    },
    onError: (error) => {
      const key = mapUploadError(error);
      toast.error(
        key === 'media.upload.err_generic' ? getApiErrorMessage(error, t(key)) : t(key),
      );
    },
  });

  return (
    <>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={MEDIA_INPUT_ACCEPT}
        className="sr-only"
        tabIndex={-1}
        aria-label={t('media.upload.input_aria')}
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Clear so picking the same file twice still fires change.
          event.target.value = '';
          if (file) uploadMutation.mutate(file);
        }}
      />
      <Button
        type="button"
        size={size}
        variant={variant}
        disabled={disabled || uploadMutation.isPending}
        onClick={() => inputRef.current?.click()}
      >
        <Upload data-icon="inline-start" />
        {uploadMutation.isPending ? t('media.upload.uploading') : t('media.upload.button')}
      </Button>
    </>
  );
}
